import { AiOutlineSearch, AiOutlineBell, AiOutlineUser } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useSearchService } from "../api/searchService";
import { useAuthService } from "../api/authService";
import { parseJwt } from "../pages/Dashboard/Dashboard";
import { FaUserCircle } from "react-icons/fa";
import { FiSettings, FiUser, FiLogOut, FiInfo } from "react-icons/fi";

export default function Navbar() {
  const token = localStorage.getItem("authToken");
  const user = parseJwt(token || "");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const { simpleSearch, searchResults, searchLoading } = useSearchService();
  const { logout } = useAuthService();

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
  };

  const handleSearch = (e: any) => {
    e.preventDefault();
    simpleSearch(searchTerm);
    setShowModal(true);
    setSearchTerm("");
  };

  const handleKeyDown = (e: any) => {
    if (e.key === "Enter") {
      handleSearch(e);
    }
  };

  return (
    <div className="bg-white px-4 shadow-md flex justify-between items-center z-50">
      <div className="flex items-center">
        <form onSubmit={handleSearch} className="relative w-48">
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            onSubmit={handleSearch}
            className="w-full px-4 my-2 text-gray-900 bg-gray-100 border border-gray-300 rounded-full focus:outline-none focus:border-purple-500"
          />
          <button
            type="submit"
            className="absolute right-3 top-3 text-gray-400"
          >
            <AiOutlineSearch />
          </button>
        </form>
      </div>
      <div className="flex items-center space-x-4 relative">
        <AiOutlineBell size={24} className="text-gray-600" />
        <div className="relative">
          <AiOutlineUser
            size={24}
            className="text-gray-600 cursor-pointer"
            onClick={() => setDropdownOpen(!dropdownOpen)}
          />
          {dropdownOpen && (
            <div
              className="absolute right-0 mt-2 w-64 bg-white border border-gray-300 rounded-lg shadow-xl overflow-hidden"
              ref={dropdownRef}
            >
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                  <FaUserCircle className="text-3xl text-gray-500" />
                  <div className="flex flex-col">
                    <span className="font-medium text-gray-900">
                      {user.username}
                    </span>
                    <span className="text-sm text-gray-500">
                      {user.teamName}
                    </span>
                  </div>
                </div>
              </div>
              <div className="py-2">
                <div className="px-4 py-2 mb-2 flex items-center space-x-3 text-white bg-gray-100 hover:bg-gray-200 rounded-full cursor-pointer transition-colors duration-150 mx-2">
                  <FiSettings className="text-lg text-gray-500" />
                  <span className="text-gray-500">Settings</span>
                </div>

                <div className="px-4 py-2 flex items-center space-x-3 text-white bg-gray-100 hover:bg-gray-200 rounded-full cursor-pointer transition-colors duration-150 mx-2">
                  <FiInfo className="text-lg text-gray-500" />
                  <span className="text-gray-500">Privacy Policy</span>
                </div>

                <div className="px-4 py-2 flex items-center space-x-3 text-white bg-gray-100 hover:bg-gray-200 rounded-full cursor-pointer transition-colors duration-150 mx-2">
                  <FiInfo className="text-lg text-gray-500" />
                  <span className="text-gray-500">Terms and Conditions</span>
                </div>

                <div
                  className="px-4 py-2 flex items-center space-x-3 text-white bg-gray-100 hover:bg-gray-200 rounded-full cursor-pointer transition-colors duration-150 mx-2"
                  onClick={() => navigate(`/profile/user/${user.username}`)}
                >
                  <FiUser className="text-lg text-gray-500" />
                  <span className="text-gray-500">Profile Page</span>
                </div>
              </div>
              <div className="border-t border-gray-200 p-2 bg-gray-50">
                <div
                  className="px-4 py-2 flex items-center space-x-3 text-gray-500 hover:bg-gray-200 rounded-full cursor-pointer transition-colors duration-150"
                  onClick={handleLogout}
                >
                  <FiLogOut className="text-lg" />
                  <span>Logout</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {showModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white p-6 rounded-lg max-w-md w-full h-2/3 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold mb-4">Search Results</h2>
            <div className="overflow-y-auto flex-grow">
              {searchLoading ? (
                <div className="flex justify-center items-center h-full">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
                </div>
              ) : (
                <div className="space-y-4 my-4">
                  {searchResults.map((result, index) => (
                    <a
                      key={index}
                      onClick={() => {
                        navigate(result.href);
                        setShowModal(false);
                      }}
                      className="card bg-base-100 bg-indigo-100 shadow-xl hover:shadow-2xl transition-shadow duration-300 cursor-pointer"
                    >
                      <div className="card-body">
                        <h3 className="card-title text-lg">{result.name}</h3>
                        <div className="badge badge-primary">{result.type}</div>
                      </div>
                    </a>
                  ))}
                  {!searchLoading && searchResults.length === 0 && (
                    <div className="text-center text-gray-500">
                      No results found.
                    </div>
                  )}
                </div>
              )}
            </div>
            <button
              onClick={() => setShowModal(false)}
              className="btn btn-primary mt-4 w-full"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
